import { useI18N } from "@sundae/react-hooks";
import { Button, Confetti, Image, Text, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, memo, useCallback, useEffect, useState } from "react";

import { CircleStackIcon } from "@heroicons/react/24/outline";
import useAppNavigate from "../../../hooks/router/useAppNavigate";
import { paths, PortfolioTab } from "../../../pages/routes";
import { useProfileAppContext } from "../../../stores";
import FadeInBasic from "../../FadeInView/FadeInBasic";

interface ITasteTestClaimedViewProps {
  isSticky?: boolean;
}

const TasteTestClaimedView: FC<ITasteTestClaimedViewProps> = ({ isSticky }) => {
  const { t } = useI18N("taste-test");
  const navigate = useAppNavigate();
  const [fireConfetti, setFireConfetti] = useState(false);
  const {
    state: { prefersReducedMotion },
  } = useProfileAppContext();

  const handleManageLiquidity = useCallback(
    () => navigate({ to: paths.root, search: { tab: PortfolioTab.liquidity } }),
    [navigate]
  );

  useEffect(() => {
    setTimeout(() => setFireConfetti(true), 500);
  }, []);

  return (
    <FadeInBasic
      className={cx(
        "relative min-h-[18rem] w-full overflow-hidden rounded-xl",
        toolkitCx.layout.background.default,
        {
          "animation-delay-100 motion-safe:animate-shadow-pulse": isSticky,
        }
      )}
    >
      <div
        className={cx(
          "relative flex w-full flex-col gap-6 pt-44",
          toolkitCx.layout.spacing.both.xl,
          toolkitCx.recipes.blurryCorner.primary
        )}
      >
        <Image
          src="/static/images/widgets/trophy.png"
          alt={t("tasteTest.claimed", { ns: "alt-tags" })}
          className={cx("absolute top-2 h-40", toolkitCx.recipes.center.horizontal)}
        />
        <div className="relative z-10 flex w-full flex-col gap-2 text-center">
          <Text className="inline-block" tag="h2" size="2xl" weight="medium">
            {t("detail.widget.claimed.title")}
          </Text>

          <Text className="inline-block" tag="p" size="sm" variant="muted">
            {t("detail.widget.claimed.copy")}
          </Text>
        </div>

        <FadeInBasic className="flex justify-center" transitionDelay={150}>
          <Button onClick={handleManageLiquidity} size="md">
            <CircleStackIcon
              className={cx(
                "h-4 w-4 will-change-transform group-hover:scale-110",
                toolkitCx.layout.transition.transform
              )}
            />
            {t("detail.widget.claimed.cta")}
          </Button>
        </FadeInBasic>
      </div>

      {!prefersReducedMotion && (
        <Confetti
          particleCount={150}
          className="!absolute bottom-0 left-0 z-[-1]"
          fire={fireConfetti}
          spread={55}
          origin={{ y: 1.05 }}
        />
      )}
    </FadeInBasic>
  );
};

export default memo(TasteTestClaimedView);
